import axios from 'axios';

// These functions were added to an object to allow them to be
// stubbed with sinon during testing.  Otherwise imported function
// can't be stubbed that way.
export default {
  /**
   * Retrieve the CSRF token from cookies.
   * @return {String} the CSRF token
   */
  getCsrfCookie() {
    const match = document.cookie.match(new RegExp('(^| )csrftoken=([^;]+)'));
    if (match) return match[2];
    return '';
  },

  rebateApiPath: '/api/1.0',

  /**
   * Get the form field configuration object from the backend.
   * @param applicationId {String} the ID for the application
   * @param type {String} the type of the form to get configuration for
   * @return {AxiosPromise<any>} a promise with a configuration object
   */
  getFormFields(applicationId, type) {
    const getFormFieldsPath = `${this.rebateApiPath}/forms/${applicationId}?form_type=${type}`;
    return axios.get(getFormFieldsPath);
  },

  /**
   * Get the forms object from the backend.
   * @param applicationId {String} the ID for the application
   * @param language {String} the language code for translation (default: 'en')
   * @return {AxiosPromise<any>} a promise with a configuration object
   */
  getForms(applicationId, language = 'en') {
    const getFormsPath = `${this.rebateApiPath}/forms/${applicationId}`;
    return axios.get(getFormsPath, { params: { lang: language } });
  },

  /**
   * Get the utiilty configuration object from the backend.
   * @param applicationId {String} the ID for the application
   * @return {AxiosPromise<any>} a promise with a configuration object
   */
  getUtilityConfig(applicationId) {
    const getUtilityConfigPath = `${this.rebateApiPath}/forms/config/${applicationId}`;
    return axios.get(getUtilityConfigPath);
  },

  /**
   * Submit the verification form data for the given application
   * @param applicationId {String} the ID for the application
   * @param formFields {Object} the verification form fields
   * @param formId {String} the form id
   * @return {AxiosPromise<any>}
   */
  submitVerificationFormData(applicationId, formFields, formId) {
    const submitFormDataPath = `${this.rebateApiPath}/applications/form/${formId}/${applicationId}`;
    const options = { headers: { 'X-CSRFToken': this.getCsrfCookie() } };
    return axios.post(submitFormDataPath, formFields, options)
      .then(response => response.data);
  },

  /**
   * Submit the reporting form data for the given application
   * @param applicationId {String} the ID for the application
   * @param formFields {Object} the verification form fields
   * @param formId {String} the form id
   * @return {AxiosPromise<any>}
   */
  submitReportingFormData(applicationId, formFields, formId) {
    const submitFormDataPath = `${this.rebateApiPath}/applications/reporting-data/${formId}/${applicationId}`;
    const options = { headers: { 'X-CSRFToken': this.getCsrfCookie() } };
    return axios.post(submitFormDataPath, formFields, options)
      .then(response => response.data);
  },

  /**
   * Submit the form data for the given application and form type
   * @param type {String} the type of the form
   * @param applicationId {String} the ID for the application
   * @param formFields {Object} the form fields
   * @param formId {String} the form id
   * @return {AxiosPromise<any>}
   */
  async submitFormData(type, applicationId, formFields, formId) {
    if (type === 'verification') {
      return this.submitVerificationFormData(applicationId, formFields, formId);
    }
    return this.submitReportingFormData(applicationId, formFields, formId);
  },

  /**
   * Queries product data for the given application
   * @param applicationId {String} the ID for the application
   * @return {AxiosPromise<any>}
   */
  getProductData(applicationId) {
    const getApplicationProductDetailsPath = `${this.rebateApiPath}/applications/product-details/${applicationId}`;
    return axios.get(getApplicationProductDetailsPath)
      .then(response => response.data);
  },

  /**
   * Gets initial customer validation data for the given application
   * @param customerId {String} the ID for the customer cache data
   * @return {AxiosPromise<any>}
   */
  getCustomerValidationData(customerId) {
    const getCustomerValidationDataPath = `${this.rebateApiPath}/customer/initial-validation-data/${customerId}`;
    return axios.get(getCustomerValidationDataPath)
      .then(response => response.data);
  },

  /**
   * Uses the validation bypass code to fetch the user's manually input data.
   * @param {string} bypasscode - the validation bypass code.
   * @return {AxiosPromise<any>}
   */
  getUserDataFromBypassCode(bypassCode, utilityID) {
    const getValidationBypassPath = `${this.rebateApiPath}/applications/validation-bypass/${bypassCode}/${utilityID}`;
    return axios.get(getValidationBypassPath).then(response => response.data);
  },

  /**
   * Retrieve the CMS text for the current utility.
   *
   * @param {number} utilityID - The utility to get the text for
   * @param {string} language - The language code for translation (default: 'en')
   * @return {Promise<AxiosResponse<Object>>} A promise that resolves to an Object of CMSTextObjects
   */
  retrieveMarketplaceText(utilityID, language = 'en') {
    return axios.get(`${this.rebateApiPath}/forms/cms_text`, {
      params: { utility_id: utilityID, lang: language },
    })
      .then(response => response.data)
      .catch(() => ({}));
  },
};
